import { useMutation, useQuery } from '@apollo/client'
import { Autocomplete, Stack, TextField, Typography } from '@mui/material'
import Link from '@tiptap/extension-link'
import Mention from '@tiptap/extension-mention'
import Underline from '@tiptap/extension-underline'
import StarterKit from '@tiptap/starter-kit'
import { FC, useRef, useState } from 'react'
import {
    LinkBubbleMenu,
    LinkBubbleMenuHandler,
    MenuButtonBold,
    MenuButtonBulletedList,
    MenuButtonEditLink,
    MenuButtonItalic,
    MenuButtonOrderedList,
    MenuButtonUnderline,
    MenuControlsContainer,
    MenuDivider,
    MenuSelectHeading,
    RichTextEditor,
    type RichTextEditorRef,
} from 'mui-tiptap'
import {
    GetScreeningInterviewTeams,
    SendCalendlyLink,
} from 'recruiter/graphql/candidateProfile.gql'
import { GetMergeFields } from 'recruiter/pages/Rosters/RosterProfile/BulkActions/BulkActions.gql'
import {
    makeMergeFieldInserter,
    makeMergeFieldRemover,
} from 'recruiter/pages/Rosters/RosterProfile/BulkActions/Blasts/makeMergeUtils'
import EmailPreferenceOrder, {
    EmailPreferenceItem,
} from 'recruiter/pages/Rosters/RosterProfile/BulkActions/Blasts/EmailPreferenceOrder'
import DrawerComponent from 'shared/components/DrawerComponent'
import MenuButton from 'shared/components/MenuButton/MenuButton'
import useToast from 'shared/components/Toast/useToast'
import { getPxAsRem } from 'shared/utils'

interface SendCalendlyDrawerProps {
    open: boolean
    onClose: () => void
    candidateId: string
    candidateName: string
    refetch: () => void
}

const DEFAULT_SUBJECT = 'Schedule a Meeting with CPI'

const DEFAULT_HTML_CONTENT = `<div><div>Hi {{firstName}},</div><div>&nbsp;</div><div>Thank you for registering in CPI\u2019s candidate portal\u2014we appreciate you taking the time to share your background with us.</div><div>&nbsp;</div><div>As a next step, we invite you to schedule an introductory meeting with our team so we can learn more about your experience and what you are looking for.</div><div>&nbsp;</div><div>You can log into the portal to select a date and time that works best for you.</div><div>&nbsp;</div><div><a href="{{schedulerLink}}">Schedule a Meeting</a></div><div>&nbsp;</div><div>We look forward to connecting.</div><div>&nbsp;</div><div>Thanks,</div><div>The CPI Team</div></div>`

const SCHEDULER_MERGE_FIELD = {
    label: 'Scheduler Link',
    mergeKey: 'schedulerLink',
}

const SendCalendlyDrawer: FC<SendCalendlyDrawerProps> = ({
    open,
    onClose,
    candidateId,
    candidateName,
    refetch,
}) => {
    const toast = useToast()
    const rteRef = useRef<RichTextEditorRef>(null)
    const [selectedTeam, setSelectedTeam] = useState<string | null>(null)
    const [subject, setSubject] = useState(DEFAULT_SUBJECT)
    const [emailPreferenceOrder, setEmailPreferenceOrder] = useState<
        EmailPreferenceItem[]
    >([
        { preference: 'preferred', enabled: true },
        { preference: 'personal', enabled: true },
        { preference: 'work', enabled: true },
    ])

    const { data: teamsData, loading: teamsLoading } = useQuery(
        GetScreeningInterviewTeams
    )
    const { data: mergeFieldData } = useQuery(GetMergeFields)
    const [sendCalendlyLink, { loading: sending }] =
        useMutation(SendCalendlyLink)

    const teams = teamsData?.screeningInterviewTeams ?? []
    const insertMergeField = makeMergeFieldInserter(rteRef.current?.editor)
    const removeMergeFields = makeMergeFieldRemover(mergeFieldData)

    const handleSend = async () => {
        if (!selectedTeam) return
        const editor = rteRef.current?.editor
        const htmlBody = editor?.getHTML() || ''
        const contentHtml = removeMergeFields(htmlBody)
        try {
            const { data } = await sendCalendlyLink({
                variables: {
                    candidateId,
                    screeningInterviewTeam: selectedTeam,
                    subject,
                    contentHtml,
                    emailPreferenceOrder: emailPreferenceOrder
                        .filter((item) => item.enabled)
                        .map((item) => item.preference),
                },
            })
            if (data?.sendCalendlyLink?.success) {
                toast({
                    title: 'Success',
                    message: `Calendly link sent to ${candidateName}`,
                    severity: 'success',
                })
                handleClose()
                refetch()
            } else {
                toast({
                    title: 'Error',
                    message:
                        data?.sendCalendlyLink?.message ??
                        'Failed to send calendly link',
                    severity: 'error',
                })
            }
        } catch (error) {
            toast({
                title: 'Error',
                message: 'Failed to send calendly link',
                severity: 'error',
            })
        }
    }

    const handleClose = () => {
        setSelectedTeam(null)
        setSubject(DEFAULT_SUBJECT)
        setEmailPreferenceOrder([
            { preference: 'preferred', enabled: true },
            { preference: 'personal', enabled: true },
            { preference: 'work', enabled: true },
        ])
        rteRef.current?.editor?.commands.setContent(DEFAULT_HTML_CONTENT)
        onClose()
    }

    return (
        <DrawerComponent
            open={open}
            onClose={handleClose}
            headerTitle="Send Calendly Link"
            primaryActionText="Send"
            onPrimaryAction={handleSend}
            saveButtonDisabled={!selectedTeam || !subject.trim() || sending}
            hasCancelButton
            onCancel={handleClose}
            width={getPxAsRem(600)}
        >
            <Stack
                gap={2}
                sx={{
                    p: 2,
                    '.tipTapMenu': {
                        justifyContent: 'space-between',
                    },
                    '.ProseMirror': {
                        minHeight: getPxAsRem(200),
                    },
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    Send a scheduling link to {candidateName}. This will set
                    their system status to Sent Scheduler.
                </Typography>
                <Autocomplete
                    options={teams as string[]}
                    value={selectedTeam}
                    onChange={(_e, value) => setSelectedTeam(value)}
                    loading={teamsLoading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Screening Interview Team"
                            placeholder="Select a team"
                        />
                    )}
                />
                <TextField
                    label="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    fullWidth
                />
                <RichTextEditor
                    ref={rteRef}
                    content={DEFAULT_HTML_CONTENT}
                    extensions={[
                        StarterKit.configure({
                            heading: { levels: [1, 2, 3] },
                            hardBreak: { keepMarks: true },
                        }),
                        Underline,
                        Link.configure({
                            openOnClick: false,
                            autolink: true,
                        }),
                        LinkBubbleMenuHandler,
                        Mention.configure({
                            renderText({ node }) {
                                return `{{${node.attrs.label ?? node.attrs.id}}}`
                            },
                            HTMLAttributes: {
                                class: 'mention',
                            },
                            deleteTriggerWithBackspace: true,
                        }),
                    ]}
                    renderControls={() => (
                        <MenuControlsContainer className="tipTapMenu">
                            <MenuSelectHeading />
                            <MenuDivider />
                            <MenuButtonBold />
                            <MenuButtonItalic />
                            <MenuButtonUnderline />
                            <MenuButtonEditLink />
                            <MenuButtonBulletedList />
                            <MenuButtonOrderedList />
                            <MenuButton
                                label="Merge Field"
                                variant="outlined"
                                size="small"
                                menuItems={[
                                    SCHEDULER_MERGE_FIELD,
                                    ...(mergeFieldData?.mergeFields ?? []),
                                ].map(({ label, mergeKey }) => ({
                                    label,
                                    onClick: () => insertMergeField(mergeKey),
                                }))}
                            />
                        </MenuControlsContainer>
                    )}
                >
                    {() => <LinkBubbleMenu />}
                </RichTextEditor>
                <EmailPreferenceOrder
                    value={emailPreferenceOrder}
                    onChange={setEmailPreferenceOrder}
                />
            </Stack>
        </DrawerComponent>
    )
}

export default SendCalendlyDrawer
